import React, { useState, useEffect } from 'react';
import './CryptoOverviewPage.css';
import AssetChart from './charts/AssetChart';
import './charts/AssetChart.css';
import LineChartWithLegend from './charts/LineChartWithLegend';
import MNAVLineChart from './charts/MNAVLineChart';
import ChartModal from './ChartModal';
import ChartLogo from './ChartLogo';
import companiesData from '../data/companies.json';
import { useGlobalData } from '../context/GlobalDataContext';

const CryptoOverviewPage = ({ cryptoType }) => {
  const [modalChart, setModalChart] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [holdingsData, setHoldingsData] = useState(null);
  const [marketCapData, setMarketCapData] = useState(null);
  const [supplyPercentagesData, setSupplyPercentagesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logScaleGrowth, setLogScaleGrowth] = useState(true);

  // Use global data context
  const { 
    getTickerTimeSeries,
    loading: globalDataLoading,
    loadingProgress,
    assetLoadingProgress,
    getAssetHoldings,
    getAssetMarketCap,
    getAssetSupplyPercentages,
    allTimeSeriesData
  } = useGlobalData();

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Wait for global data to be loaded
        if (globalDataLoading) {
          return;
        }
        
        // Use data from global context (already loaded in Phase 3)
        const holdings = getAssetHoldings(cryptoType);
        const marketCap = getAssetMarketCap(cryptoType);
        const supplyPercentages = getAssetSupplyPercentages(cryptoType);
        
        setHoldingsData(holdings);
        setMarketCapData(marketCap);
        setSupplyPercentagesData(supplyPercentages);
      } catch (error) {
        // Error loading data
      } finally {
        setLoading(false);
      }
    };

    if (cryptoType && !globalDataLoading) {
      fetchData();
    }
  }, [cryptoType, globalDataLoading, getAssetHoldings, getAssetMarketCap, getAssetSupplyPercentages]);

  const getCryptoData = () => {
    const companies = (companiesData[cryptoType] || []).filter(c => c.trackData === true);
    
    const formatHoldings = (holdings) => {
      if (!holdings || holdings === 0) return 'N/A';
      return holdings.toLocaleString('en-US', { maximumFractionDigits: 0 });
    };

    const formatMarketCap = (marketCap) => {
      if (!marketCap || marketCap === 0) return 'N/A';
      
      if (marketCap >= 1e12) {
        return '$' + (marketCap / 1e12).toFixed(1) + 'T';
      } else if (marketCap >= 1e9) {
        return '$' + (marketCap / 1e9).toFixed(1) + 'B';
      } else if (marketCap >= 1e6) {
        return '$' + (marketCap / 1e6).toFixed(1) + 'M';
      }
      return '$' + marketCap.toLocaleString();
    };

    const calculateSupplyPercentage = () => {
      // For all assets, sum the individual company percentages instead of calculating from supply data
      if (supplyPercentagesData?.totalPercentage !== undefined) {
        return supplyPercentagesData.totalPercentage.toFixed(2) + '%';
      } else {
        return 'N/A';
      }
    };
    
    switch (cryptoType) {
      case 'bitcoin':
        return {
          name: 'Bitcoin',
          ticker: 'BTC',
          color: '#f7931a',
          totalCompanies: companies.length.toString(),
          totalHoldings: formatHoldings(holdingsData?.totalHoldings),
          marketCap: formatMarketCap(marketCapData),
          supplyPercentage: calculateSupplyPercentage()
        };
      case 'ethereum':
        return {
          name: 'Ethereum',
          ticker: 'ETH',
          color: '#627eea',
          totalCompanies: companies.length.toString(),
          totalHoldings: formatHoldings(holdingsData?.totalHoldings),
          marketCap: formatMarketCap(marketCapData),
          supplyPercentage: calculateSupplyPercentage()
        };
      case 'solana':
        return {
          name: 'Solana',
          ticker: 'SOL',
          color: '#9945ff',
          totalCompanies: companies.length.toString(),
          totalHoldings: formatHoldings(holdingsData?.totalHoldings),
          marketCap: formatMarketCap(marketCapData),
          supplyPercentage: calculateSupplyPercentage()
        };
      default:
        return {
          name: 'Crypto',
          ticker: 'CRYPTO',
          color: '#00ffff',
          totalCompanies: 'N/A',
          totalHoldings: 'N/A',
          marketCap: 'N/A',
          supplyPercentage: 'N/A'
        };
    }
  };

  // Get holdings over time for the current asset type with separate lines for each ticker
  const getAssetHoldingsData = () => {
    if (!companiesData[cryptoType]) return [];

    const assetCompanies = companiesData[cryptoType].filter(c => c.trackData === true);
    const cutoffDate = cryptoType === 'bitcoin' ? new Date('2025-06-15') : null;
    const allDates = new Set();
    const tickerData = {};

    // Get time series data for each company in this asset type
    assetCompanies.forEach(company => {
      const ticker = company.ticker;
      const timeSeries = getTickerTimeSeries(ticker);
      
      if (!timeSeries || timeSeries.length === 0) return;
      
      // Use crypto_holdings column from time series data
      timeSeries.forEach(row => {
        if (!row.date || row.crypto_holdings === null || row.crypto_holdings === undefined) {
          return;
        }
        if (cutoffDate) {
          const rowDate = new Date(row.date);
          if (!(rowDate >= cutoffDate)) {
            return;
          }
        }
        allDates.add(row.date);
        if (!tickerData[ticker]) {
          tickerData[ticker] = {};
        }
        tickerData[ticker][row.date] = row.crypto_holdings;
      });
    });

    // Convert to array format for LineChartWithLegend
    const sortedDates = Array.from(allDates).sort((a, b) => new Date(a) - new Date(b));
    if (sortedDates.length === 0) return [];
    
    return sortedDates.map(date => {
      const row = { date };
      assetCompanies.forEach(company => {
        const ticker = company.ticker;
        row[ticker] = tickerData[ticker]?.[date] || null;
      });
      return row;
    });
  };

  // Get crypto per share data for the current asset type with separate lines for each ticker
  const getAssetPerShareData = () => {
    if (!companiesData[cryptoType]) return [];

    const assetCompanies = companiesData[cryptoType].filter(c => c.trackData === true);
    const cutoffDate = cryptoType === 'bitcoin' ? new Date('2025-06-15') : null;
    const allDates = new Set();
    const tickerData = {};

    // Get time series data for each company in this asset type
    assetCompanies.forEach(company => {
      const ticker = company.ticker;
      const timeSeries = getTickerTimeSeries(ticker);
      
      if (!timeSeries || timeSeries.length === 0) return;
      
      // Use crypto_per_share column from time series data
      timeSeries.forEach(row => {
        if (!row.date || row.crypto_per_share === null || row.crypto_per_share === undefined) {
          return;
        }
        if (cutoffDate) {
          const rowDate = new Date(row.date);
          if (!(rowDate >= cutoffDate)) {
            return;
          }
        }
        allDates.add(row.date);
        if (!tickerData[ticker]) {
          tickerData[ticker] = {};
        }
        tickerData[ticker][row.date] = row.crypto_per_share;
      });
    });

    // Convert to array format for LineChartWithLegend
    const sortedDates = Array.from(allDates).sort((a, b) => new Date(a) - new Date(b));
    if (sortedDates.length === 0) return [];
    
    return sortedDates.map(date => {
      const row = { date };
      assetCompanies.forEach(company => {
        const ticker = company.ticker;
        row[ticker] = tickerData[ticker]?.[date] || null;
      });
      return row;
    });
  };

  // Get ticker names for the legend
  const getTickerNames = () => {
    return (companiesData[cryptoType] || []).filter(c => c.trackData === true).map(company => company.ticker) || [];
  };

  // Get colors for tickers (same as MNAV chart)
  const getTickerColors = () => {
    // Get color palette based on asset type
    const getColorPalette = () => {
      switch (cryptoType) {
        case 'bitcoin':
          return [
            '#ffff00', // Yellow
            '#ffeb3b', // Light yellow
            '#ffc107', // Amber
            '#ff9800', // Orange
            '#ff4500', // Red-orange
            '#f44336', // Red
            '#dc143c', // Crimson red
            '#ff1493', // Deep pink
            '#ff69b4', // Hot pink
            '#32cd32', // Lime green
            '#00ff7f', // Spring green
            '#00ff00', // Green
            '#228b22', // Forest green
            '#006400', // Dark green
            '#00ced1', // Dark turquoise
            '#40e0d0'  // Turquoise
          ];
        case 'ethereum':
          return [
            '#add8e6',   // Light blue
            '#87ceeb',   // Sky blue
            '#6495ed',   // Cornflower blue
            '#4682b4',   // Steel blue
            '#4169e1',   // Royal blue
            '#627eea',   // Ethereum blue
            '#483d8b',   // Dark slate blue
            '#4b0082',   // Indigo
            '#8a2be2',   // Blue violet
            '#800080'    // Purple
          ];
        case 'solana':
          return [
            '#32cd32',   // Lime green (bright green)
            '#2ecc71',   // Emerald green
            '#00c9a7',   // Turquoise
            '#48d1cc',   // Medium turquoise
            '#1e90ff',   // Dodger blue
            '#4682b4',   // Steel blue
            '#8a2be2',   // Blue violet
            '#800080'    // Purple
          ];
        default:
          return ['#6b7280'];
      }
    };

    const colorPalette = getColorPalette();
    const assetCompanies = (companiesData[cryptoType] || []).filter(c => c.trackData === true);
    
    // Return colors based on index, cycling through the palette
    return assetCompanies.map((company, index) => {
      const color = colorPalette[index % colorPalette.length];
      return { color, dash: 'solid' };
    });
  };

  const cryptoData = getCryptoData();
  const assetHoldingsData = getAssetHoldingsData();
  const assetPerShareData = getAssetPerShareData();

  // Check if we have critical data loaded
  // Allow empty arrays/zeros, just check that data exists (not null/undefined)
  const hasDataLoaded = holdingsData !== null && holdingsData !== undefined &&
                        marketCapData !== null && marketCapData !== undefined &&
                        supplyPercentagesData !== null && supplyPercentagesData !== undefined;
  
  // Check if asset-specific time series data is loaded
  // For now, just check if we have any time series data (don't require asset-specific loading)
  const hasTimeSeries = allTimeSeriesData !== null && allTimeSeriesData.length > 0;

  return (
    <div className="crypto-overview-page" style={{ '--crypto-color': cryptoData.color }}>
      {/* Loading Overlay */}
      {(loading || globalDataLoading || !hasDataLoaded || !hasTimeSeries) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-progress-container">
              {/* Use assetLoadingProgress if global loading is done, otherwise use global loadingProgress */}
              <div className="loading-progress-bar" style={{ width: `${(!globalDataLoading && !loading) ? assetLoadingProgress : (loadingProgress || 0)}%` }}></div>
            </div>
            <div className="loading-text">Loading Data</div>
            <div className="loading-percentage">{Math.round((!globalDataLoading && !loading) ? assetLoadingProgress : (loadingProgress || 0))}%</div>
          </div>
        </div>
      )}

      {/* Main Content - Only show when not loading */}
      {!loading && !globalDataLoading && hasDataLoaded && hasTimeSeries && (
        <>
      {/* Summary Cards Row */}
      <div className="summary-cards-row">
        <div className="summary-card crypto-card" style={{ '--crypto-color': cryptoData.color }}>
          <h3 className="summary-card-title">{cryptoData.name} Companies Tracked</h3>
          <p className="summary-card-description">Public companies holding {cryptoData.name}</p>
          <p className="crypto-value" style={{ color: cryptoData.color }}>{cryptoData.totalCompanies}</p>
        </div>

        <div className="summary-card crypto-card" style={{ '--crypto-color': cryptoData.color }}>
          <h3 className="summary-card-title">Combined Market Cap</h3>
          <p className="summary-card-description">Total market value of {cryptoData.name} holdings</p>
          <p className="crypto-value" style={{ color: cryptoData.color }}>{cryptoData.marketCap}</p>
        </div>

        <div className="summary-card crypto-card" style={{ '--crypto-color': cryptoData.color }}>
          <h3 className="summary-card-title">Total {cryptoData.ticker}<br />Holdings</h3>
          <p className="summary-card-description">Combined {cryptoData.ticker} held by tracked companies</p>
          <p className="crypto-value" style={{ color: cryptoData.color }}>{cryptoData.totalHoldings}</p>
        </div>

        <div className="summary-card crypto-card" style={{ '--crypto-color': cryptoData.color }}>
          <h3 className="summary-card-title">% of Circulating Supply</h3>
          <p className="summary-card-description">Percentage of total {cryptoData.ticker} supply held by companies</p>
          <p className="crypto-value" style={{ color: cryptoData.color }}>{cryptoData.supplyPercentage}</p>
        </div>
      </div>

      {/* Asset Analytics Chart */}
      <AssetChart assetType={cryptoType} />

      {/* Additional Overview Charts */}
      <div className="additional-charts-section">
        <div className="chart-container" onClick={() => !isMobile && setModalChart('mnav')}>
          <div className="chart-logo-wrapper">
            <ChartLogo />
          </div>
          <MNAVLineChart 
            assetType={cryptoType}
            title="MNAV Over Time" 
          />
        </div>

        {/* Growth Charts Section */}
        <div style={{ marginTop: '2rem' }}>
          <div className="charts-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 className="charts-title">Growth Charts</h2>
            <div className="proforma-toggle">
              <span className="toggle-label">
                Y-Axis Log Scale
              </span>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={logScaleGrowth}
                  onChange={(e) => setLogScaleGrowth(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="charts-grid">
            <div className="chart-container" onClick={() => !isMobile && setModalChart('holding_growth')}>
              <div className="chart-logo-wrapper">
                <ChartLogo />
              </div>
              <LineChartWithLegend 
                title={`${cryptoData.ticker} Holdings`} 
                data={assetHoldingsData}
                xKey="date"
                yKeys={getTickerNames()}
                colors={getTickerColors()}
                showLegend={true}
                xLabel="Date"
                yLabel={`${cryptoData.ticker} Holdings`}
                logScale={logScaleGrowth}
              />
            </div>
            
            <div className="chart-container" onClick={() => !isMobile && setModalChart('per_share')}>
              <div className="chart-logo-wrapper">
                <ChartLogo />
              </div>
              <LineChartWithLegend 
                title={`${cryptoData.ticker} per Share`} 
                data={assetPerShareData}
                xKey="date"
                yKeys={getTickerNames()}
                colors={getTickerColors()}
                showLegend={true}
                xLabel="Date"
                yLabel={`${cryptoData.ticker} per Share`}
                logScale={logScaleGrowth}
              />
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Chart Modals */}
      <ChartModal isOpen={modalChart === 'mnav'} onClose={() => setModalChart(null)}>
        <MNAVLineChart 
          assetType={cryptoType}
          title="MNAV Over Time" 
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'holding_growth'} onClose={() => setModalChart(null)}>
        <LineChartWithLegend 
          title={`${cryptoData.ticker} Holdings`} 
          data={assetHoldingsData}
          xKey="date"
          yKeys={getTickerNames()}
          colors={getTickerColors()}
          showLegend={true}
          xLabel="Date"
          yLabel={`${cryptoData.ticker} Holdings`}
          logScale={logScaleGrowth}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'per_share'} onClose={() => setModalChart(null)}>
        <LineChartWithLegend 
          title={`${cryptoData.ticker} per Share`} 
          data={assetPerShareData}
          xKey="date"
          yKeys={getTickerNames()}
          colors={getTickerColors()}
          showLegend={true}
          xLabel="Date"
          yLabel={`${cryptoData.ticker} per Share`}
          logScale={logScaleGrowth}
        />
      </ChartModal>
    </div>
  );
};

export default CryptoOverviewPage;
